import { useState, useEffect, useMemo, useCallback } from 'react';
import { Layout } from '@/components/layout/Layout';
import { BotaoFlutuante } from '@/components/layout/BotaoFlutuante';
import {
  UserPlus,
  RefreshCw,
  Lock,
  AlertTriangle,
  Trash2,
  Unlock,
  UsersRound,
  Search,
  SlidersHorizontal,
  Check,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from 'lucide-react';
import { UserTableRow } from './UserTableRow';
import { UserCard } from './UserCard';
import { EditUserDrawer } from './EditUserDrawer';
import { UserDetailDrawer } from './UserDetailDrawer';
import { NewUserWizardDrawer } from './NewUserWizardDrawer';
import { ManageSubscriptionModal } from './ManageSubscriptionModal';
import { Modal } from '@/components/common/Modal';
import { ListaMobile } from '@/components/common/ListaMobile';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  useUsers,
  useUser,
  useUserJourney,
  useUpdateUser,
  useBlockUser,
  useUnblockUser,
  useDeleteUser,
} from '@/hooks/useUsers';
import { useUserFuncionarios } from '@/hooks/useSubscription';
import { ordenarUsuarios, proximaOrdenacao } from './ordenacao';
import { passaNoFiltro, temAcesso } from './userDisplay';
import type { FiltroUsuario } from './userDisplay';
import type { ColunaOrdenavel, Ordenacao } from './ordenacao';

// Classe base dos inputs/textarea — tokenizada p/ dark mode.
// Campo = fill `muted` (well distinto do modal `elevated`) definido pela borda forte,
// como no protótipo (borda define o campo; fill próximo da superfície, não preto).
const inputClass =
  'w-full rounded-lg border border-border-strong bg-muted px-4 py-2 text-sm text-text-title placeholder:text-text-hint transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30';

const labelClass = 'mb-2 block text-sm font-medium text-text-title';

// Ativo = trial ou assinatura valendo; Inativo = o resto (expirada, cancelada,
// sem assinatura). Bloqueado é a conta, independente do plano.
const STATUS_FILTERS: { value: FiltroUsuario; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'ativo', label: 'Ativo' },
  { value: 'inativo', label: 'Inativo' },
  { value: 'blocked', label: 'Bloqueado' },
];

// A API pagina em 50 por padrão e a tela não tem paginação: pede todos de uma
// vez, senão os usuários além do 50º somem da lista e dos filtros.
const LIMITE_USUARIOS = 1000;

// Audiência = produtor (dono de propriedade) vs funcionário (vinculado). Server-side.
const AUDIENCE_FILTERS: { value: 'all' | 'produtores' | 'funcionarios'; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'produtores', label: 'Produtores' },
  { value: 'funcionarios', label: 'Funcionários' },
];

/**
 * Cabeçalho que ordena a lista ao ser clicado.
 *
 * É um `button` de verdade dentro do `th`, não um `onClick` na célula: assim
 * chega pelo Tab e responde ao Enter, e o leitor de tela anuncia a ordem atual
 * pelo `aria-sort`. A seta some quando a coluna não está ordenando — ficar
 * apontando pra baixo sem estar ativa é o tipo de coisa que confunde.
 */
function ThOrdenavel({
  coluna,
  ordenacao,
  onOrdenar,
  children,
}: {
  coluna: ColunaOrdenavel;
  ordenacao: Ordenacao | null;
  onOrdenar: (coluna: ColunaOrdenavel) => void;
  children: React.ReactNode;
}) {
  const ativa = ordenacao?.coluna === coluna;
  const asc = ativa && ordenacao?.direcao === 'asc';
  const Icone = !ativa ? ChevronsUpDown : asc ? ChevronUp : ChevronDown;

  return (
    <th className="px-5 py-3" aria-sort={!ativa ? 'none' : asc ? 'ascending' : 'descending'}>
      <button
        type="button"
        onClick={() => onOrdenar(coluna)}
        title={ativa ? 'Clique para inverter, e de novo para tirar a ordenação' : 'Ordenar'}
        className={cn(
          'group -mx-1 flex items-center gap-1.5 rounded px-1 py-0.5 text-xs font-semibold uppercase tracking-wider transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
          ativa ? 'text-primary' : 'text-text-secondary hover:text-text-title'
        )}
      >
        {children}
        <Icone
          size={14}
          className={cn(
            'shrink-0 transition-opacity',
            ativa ? 'opacity-100' : 'opacity-0 group-hover:opacity-60'
          )}
        />
      </button>
    </th>
  );
}

export function Admin() {
  const [selectedFilter, setSelectedFilter] = useState<FiltroUsuario>('all');
  const [audience, setAudience] = useState<'all' | 'produtores' | 'funcionarios'>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [subscriptionUser, setSubscriptionUser] = useState<{ id: number; name: string } | null>(
    null
  );
  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // Edit form state
  const [editFormData, setEditFormData] = useState({
    name: '',
    phone: '',
    cargo: '',
    property: {
      name: '',
      city: '',
      uf: '',
      area_total: 0,
      reserva_legal: 0,
      sistema_ilpf: 0,
      pastagem_ha: 0,
      area_agricola: 0,
    },
  });

  // Block form state
  const [blockReason, setBlockReason] = useState('');

  // Debounce da busca (evita um request por tecla).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // O filtro de status roda no cliente (depende da assinatura, que a API não filtra).
  const { data, isLoading, error } = useUsers({
    limit: LIMITE_USUARIOS,
    onlyProducers: audience === 'produtores' || undefined,
    onlyFuncionarios: audience === 'funcionarios' || undefined,
    search: debouncedSearch || undefined,
  });

  const { data: selectedUserData } = useUser(selectedUserId || '');
  // Dados extras do drawer de detalhes (só buscam quando o drawer está aberto).
  // useUserJourney já mescla atividade recente + navegação (telas visitadas).
  const { data: detailActivity, isLoading: detailActivityLoading } = useUserJourney(
    selectedUserId || '',
    showDetailsModal
  );
  const { data: detailFuncionarios } = useUserFuncionarios(selectedUserId, showDetailsModal);

  const updateUserMutation = useUpdateUser();
  const blockUserMutation = useBlockUser();
  const unblockUserMutation = useUnblockUser();
  const deleteUserMutation = useDeleteUser();

  useEffect(() => {
    // User data loaded
  }, [selectedUserData]);

  useEffect(() => {
    if (selectedUserData && showEditModal) {
      const propertyData =
        typeof selectedUserData.property === 'string'
          ? {
              name: selectedUserData.property,
              city: '',
              uf: '',
              area_total: 0,
              reserva_legal: 0,
              sistema_ilpf: 0,
              pastagem_ha: 0,
              area_agricola: 0,
            }
          : selectedUserData.property;

      setEditFormData({
        name: selectedUserData.name || '',
        phone: selectedUserData.phone || '',
        cargo: selectedUserData.cargo || selectedUserData.role || '',
        property: {
          name: propertyData?.name || '',
          city: propertyData?.city || '',
          uf: propertyData?.uf || '',
          area_total: propertyData?.area_total || 0,
          reserva_legal: propertyData?.reserva_legal || 0,
          sistema_ilpf: propertyData?.sistema_ilpf || 0,
          pastagem_ha: propertyData?.pastagem_ha || 0,
          area_agricola: propertyData?.area_agricola || 0,
        },
      });
    }
  }, [selectedUserData, showEditModal]);

  // O `|| []` criava um array novo a cada render, e o useMemo da ordenação
  // reordenava a lista inteira toda vez à toa.
  const todos = useMemo(() => data?.users ?? [], [data?.users]);
  const users = useMemo(
    () => todos.filter((u) => passaNoFiltro(u, selectedFilter)),
    [todos, selectedFilter]
  );

  // Ordenação por clique no cabeçalho. Fica no cliente porque a lista do admin
  // é pequena e já vem inteira; virando centenas, isto passa pro servidor.
  const [ordenacao, setOrdenacao] = useState<Ordenacao | null>(null);
  const alternarOrdem = useCallback(
    (coluna: ColunaOrdenavel) => setOrdenacao((atual) => proximaOrdenacao(atual, coluna)),
    []
  );
  const usersOrdenados = useMemo(() => ordenarUsuarios(users, ordenacao), [users, ordenacao]);

  // Contadores do topo: sobre a lista inteira (busca e audiência), não sobre o
  // filtro de status, para não virar "12 usuários · 12 ativos".
  const totalUsers = todos.length;
  const activeCount = todos.filter(temAcesso).length;
  const blockedCount = todos.filter((u) => u.status === 'blocked').length;
  const verifiedCount = todos.filter((u) => u.verified).length;

  const handleBlockUser = async () => {
    if (!selectedUserId || !blockReason || blockReason.length < 10) {
      alert('Por favor, forneça um motivo com pelo menos 10 caracteres');
      return;
    }

    try {
      await blockUserMutation.mutateAsync({
        id: selectedUserId,
        data: {
          reason: blockReason,
          revoke_sessions: true,
        },
      });
      setShowBlockModal(false);
      setBlockReason('');
      setSelectedUserId(null);
    } catch (err) {
      console.error('Error blocking user:', err);
      alert('Erro ao bloquear usuário. Tente novamente.');
    }
  };

  const handleUnblockUser = (userId: string) => {
    setSelectedUserId(userId);
    setShowUnblockModal(true);
  };

  const confirmUnblockUser = async () => {
    if (!selectedUserId) return;

    try {
      await unblockUserMutation.mutateAsync(selectedUserId);
      setShowUnblockModal(false);
      setSelectedUserId(null);
    } catch (err) {
      console.error('Error unblocking user:', err);
      alert('Erro ao desbloquear usuário. Tente novamente.');
    }
  };

  const handleDeleteUser = (userId: string) => {
    setSelectedUserId(userId);
    setShowDeleteModal(true);
  };

  const confirmDeleteUser = async () => {
    if (!selectedUserId) return;

    try {
      await deleteUserMutation.mutateAsync(selectedUserId);
      setShowDeleteModal(false);
      setSelectedUserId(null);
    } catch (err) {
      console.error('Error deleting user:', err);
      alert('Erro ao excluir usuário. Tente novamente.');
    }
  };

  const handleViewDetails = (userId: string) => {
    setSelectedUserId(userId);
    setShowDetailsModal(true);
  };

  const handleEditUser = (userId: string) => {
    setSelectedUserId(userId);
    setShowEditModal(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUserId) return;

    try {
      await updateUserMutation.mutateAsync({
        id: selectedUserId,
        data: editFormData,
      });
      setShowEditModal(false);
      setSelectedUserId(null);
    } catch (err) {
      console.error('Error updating user:', err);
      alert('Erro ao atualizar usuário. Tente novamente.');
    }
  };

  if (error) {
    return (
      <Layout
        breadcrumbs={[{ label: 'Admin', path: '/admin' }, { label: 'Gerenciamento de Usuários' }]}
      >
        <div className="flex flex-col gap-4 lg:gap-6 p-3 sm:p-4 lg:p-6">
          <div className="flex items-center justify-center py-24">
            <div className="flex max-w-sm flex-col items-center gap-4 text-center">
              <span className="flex size-14 items-center justify-center rounded-2xl bg-error/10 text-error">
                <AlertTriangle size={28} />
              </span>
              <div>
                <h2 className="mb-1 text-xl font-semibold text-text-title">
                  Erro ao carregar usuários
                </h2>
                <p className="text-sm text-text-secondary">
                  Não foi possível carregar a lista de usuários. Tente novamente.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      breadcrumbs={[{ label: 'Admin', path: '/admin' }, { label: 'Gerenciamento de Usuários' }]}
    >
      <div className="flex flex-col gap-4 lg:gap-6 p-3 sm:p-4 lg:p-6">
        {/* Header: título + stats inline */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <h1 className="max-md:hidden text-2xl font-bold tracking-tight text-text-title">
              Usuários
            </h1>
            {/* Celular/tablet: uma linha de resumo, como no desenho aprovado. */}
            <p className="text-xs text-text-secondary lg:hidden">
              <span className="tabular-nums">{totalUsers}</span>{' '}
              {totalUsers === 1 ? 'usuário' : 'usuários'} ·{' '}
              <span className="tabular-nums text-success">
                {activeCount} {activeCount === 1 ? 'ativo' : 'ativos'}
              </span>{' '}
              ·{' '}
              <span className="tabular-nums text-error">
                {blockedCount} {blockedCount === 1 ? 'bloqueado' : 'bloqueados'}
              </span>
            </p>
            <div className="hidden flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-text-secondary lg:flex">
              <span>
                <span className="font-semibold tabular-nums text-text-title">{totalUsers}</span>{' '}
                total
              </span>
              <span>
                <span className="font-semibold tabular-nums text-success">{activeCount}</span>{' '}
                ativos
              </span>
              <span>
                <span className="font-semibold tabular-nums text-error">{blockedCount}</span>{' '}
                bloqueados
              </span>
              <span>
                <span className="font-semibold tabular-nums text-info">{verifiedCount}</span>{' '}
                verificados
              </span>
            </div>
          </div>
          <Button
            onClick={() => setShowNewUserModal(true)}
            className="shrink-0 active:scale-[0.98] max-md:hidden"
          >
            <UserPlus size={18} />
            <span>Novo usuário</span>
          </Button>
          <BotaoFlutuante
            icon={UserPlus}
            label="Novo usuário"
            onClick={() => setShowNewUserModal(true)}
          />
        </div>

        {/* Busca + audiência + filtros */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar usuário..."
              className="w-full rounded-lg border border-border bg-card py-2.5 pl-9 pr-3 text-sm text-text-title placeholder:text-text-hint transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div className="flex items-center gap-2">
            <div
              className="flex rounded-lg border border-border bg-card p-1 max-md:flex-1"
              role="group"
              aria-label="Filtrar por tipo"
            >
              {AUDIENCE_FILTERS.map((a) => {
                const active = audience === a.value;
                return (
                  <button
                    key={a.value}
                    onClick={() => setAudience(a.value)}
                    aria-pressed={active}
                    className={cn(
                      'rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 max-md:flex-1 max-md:px-2',
                      active
                        ? 'bg-primary text-bg-main shadow-card'
                        : 'text-text-secondary hover:text-text-title'
                    )}
                  >
                    {a.label}
                  </button>
                );
              })}
            </div>
            <div className="relative">
              <button
                onClick={() => setShowFilters((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={showFilters}
                className={cn(
                  'flex h-9 items-center gap-1.5 rounded-lg border px-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                  selectedFilter !== 'all'
                    ? 'border-primary/50 bg-primary/10 text-primary'
                    : 'border-border bg-card text-text-secondary hover:bg-muted'
                )}
              >
                <SlidersHorizontal size={16} />
                <span className="max-md:sr-only">Filtros</span>
                {selectedFilter !== 'all' && <span className="size-1.5 rounded-full bg-primary" />}
              </button>
              {showFilters && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowFilters(false)} />
                  <div
                    role="menu"
                    className="absolute right-0 z-50 mt-2 w-48 overflow-hidden rounded-xl border border-border bg-bg-elevated p-1 shadow-elevated animate-fade-up"
                  >
                    <p className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-text-hint">
                      Status
                    </p>
                    {STATUS_FILTERS.map((f) => {
                      const active = selectedFilter === f.value;
                      return (
                        <button
                          key={f.value}
                          role="menuitemradio"
                          aria-checked={active}
                          onClick={() => {
                            setSelectedFilter(f.value);
                            setShowFilters(false);
                          }}
                          className={cn(
                            'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-muted',
                            active ? 'font-medium text-primary' : 'text-text-body'
                          )}
                        >
                          {f.label}
                          {active && <Check size={15} />}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Lista: tabela (desktop) + ListaMobile (celular/tablet). Com dados, o
            cartão de fora sai: a tabela tem o seu e a ListaMobile já é um bloco. */}
        <div
          className={cn(
            'w-full',
            (isLoading || users.length === 0) &&
              'overflow-hidden rounded-xl border border-border bg-card shadow-card'
          )}
        >
          {isLoading ? (
            <div className="divide-y divide-border">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-5 py-4">
                  <div className="size-10 shrink-0 animate-pulse rounded-full bg-muted" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3.5 w-40 animate-pulse rounded bg-muted" />
                    <div className="h-3 w-28 animate-pulse rounded bg-muted" />
                  </div>
                  <div className="hidden h-5 w-24 animate-pulse rounded-full bg-muted sm:block" />
                </div>
              ))}
            </div>
          ) : users.length > 0 ? (
            <>
              {/* Desktop */}
              <div className="hidden overflow-hidden rounded-xl border border-border bg-card shadow-card lg:block">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-border bg-muted/40 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                      <tr>
                        <ThOrdenavel coluna="nome" ordenacao={ordenacao} onOrdenar={alternarOrdem}>
                          Usuário
                        </ThOrdenavel>
                        <ThOrdenavel
                          coluna="assinatura"
                          ordenacao={ordenacao}
                          onOrdenar={alternarOrdem}
                        >
                          Assinatura
                        </ThOrdenavel>
                        <ThOrdenavel
                          coluna="propriedade"
                          ordenacao={ordenacao}
                          onOrdenar={alternarOrdem}
                        >
                          Propriedade
                        </ThOrdenavel>
                        <ThOrdenavel
                          coluna="ultimoAcesso"
                          ordenacao={ordenacao}
                          onOrdenar={alternarOrdem}
                        >
                          Último acesso
                        </ThOrdenavel>
                        <th className="px-5 py-3 text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {usersOrdenados.map((user) => (
                        <UserTableRow
                          key={user.id}
                          user={user}
                          onViewDetails={handleViewDetails}
                          onEdit={handleEditUser}
                          onBlock={(id) => {
                            setSelectedUserId(id);
                            setShowBlockModal(true);
                          }}
                          onUnblock={handleUnblockUser}
                          onDelete={handleDeleteUser}
                          onManageSubscription={(id) => {
                            const u = users.find((x) => x.id === id);
                            if (u) setSubscriptionUser({ id: Number(u.id), name: u.name });
                          }}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Mobile */}
              <ListaMobile className="lg:hidden">
                {usersOrdenados.map((user) => (
                  <UserCard
                    key={user.id}
                    user={user}
                    onViewDetails={handleViewDetails}
                    onEdit={handleEditUser}
                    onBlock={(id) => {
                      setSelectedUserId(id);
                      setShowBlockModal(true);
                    }}
                    onUnblock={handleUnblockUser}
                    onDelete={handleDeleteUser}
                    onManageSubscription={(id) => {
                      const u = users.find((x) => x.id === id);
                      if (u) setSubscriptionUser({ id: Number(u.id), name: u.name });
                    }}
                  />
                ))}
              </ListaMobile>
            </>
          ) : (
            <div className="flex items-center justify-center py-16">
              <div className="flex max-w-xs flex-col items-center gap-4 text-center">
                <span className="flex size-14 items-center justify-center rounded-2xl bg-muted text-text-secondary">
                  <UsersRound size={28} />
                </span>
                <div>
                  <p className="mb-1 text-lg font-semibold text-text-title">
                    Nenhum usuário encontrado
                  </p>
                  <p className="text-sm text-text-secondary">
                    Ajuste a busca ou os filtros para ver mais resultados.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* New User Wizard Drawer */}
        <NewUserWizardDrawer isOpen={showNewUserModal} onClose={() => setShowNewUserModal(false)} />

        {/* Block User Modal */}
        <Modal
          isOpen={showBlockModal}
          onClose={() => {
            if (blockUserMutation.isPending) return;
            setShowBlockModal(false);
            setBlockReason('');
            setSelectedUserId(null);
          }}
          title="Bloquear Usuário"
          size="sm"
        >
          <div className="p-6">
            <div className="mb-4 flex items-start gap-3 rounded-lg border border-error/20 bg-error/10 p-3">
              <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-error/15 text-error">
                <Lock size={18} />
              </span>
              <p className="text-sm text-text-body">
                Todas as sessões do usuário serão invalidadas e ele perderá o acesso ao sistema.
              </p>
            </div>
            <label className={labelClass}>Motivo do Bloqueio *</label>
            <textarea
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              className={inputClass}
              rows={4}
              placeholder="Mínimo 10 caracteres"
              minLength={10}
              required
            />
          </div>

          <div className="flex justify-end gap-3 border-t border-border bg-bg-elevated p-6">
            <Button
              variant="secondary"
              onClick={() => {
                setShowBlockModal(false);
                setBlockReason('');
                setSelectedUserId(null);
              }}
              disabled={blockUserMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleBlockUser}
              disabled={blockUserMutation.isPending || blockReason.length < 10}
            >
              {blockUserMutation.isPending && <RefreshCw size={16} className="animate-spin" />}
              {blockUserMutation.isPending ? 'Bloqueando...' : 'Bloquear'}
            </Button>
          </div>
        </Modal>

        {/* View Details Drawer */}
        <UserDetailDrawer
          isOpen={showDetailsModal && !!selectedUserData}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedUserId(null);
          }}
          user={selectedUserData}
          activity={detailActivity}
          activityLoading={detailActivityLoading}
          funcionariosCount={detailFuncionarios?.length ?? null}
          onEdit={() => {
            setShowDetailsModal(false);
            setShowEditModal(true);
          }}
          onManageSubscription={() => {
            if (selectedUserData) {
              setSubscriptionUser({ id: Number(selectedUserData.id), name: selectedUserData.name });
            }
            setShowDetailsModal(false);
          }}
          onBlock={() => {
            setShowDetailsModal(false);
            setShowBlockModal(true);
          }}
        />

        {/* Edit User Drawer */}
        <EditUserDrawer
          isOpen={showEditModal && !!selectedUserData}
          onClose={() => {
            if (updateUserMutation.isPending) return;
            setShowEditModal(false);
            setSelectedUserId(null);
          }}
          user={selectedUserData}
          formData={editFormData}
          onFormDataChange={setEditFormData}
          onSave={handleUpdateUser}
          onDelete={() => {
            if (!selectedUserId) return;
            setShowEditModal(false);
            handleDeleteUser(selectedUserId);
          }}
          isSaving={updateUserMutation.isPending}
        />

        {/* Unblock User Confirmation Modal */}
        <Modal
          isOpen={showUnblockModal}
          onClose={() => {
            if (unblockUserMutation.isPending) return;
            setShowUnblockModal(false);
            setSelectedUserId(null);
          }}
          title="Desbloquear Usuário"
          size="sm"
        >
          <div className="p-6">
            <div className="flex items-start gap-3 rounded-lg border border-success/20 bg-success/10 p-3">
              <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-success/15 text-success">
                <Unlock size={18} />
              </span>
              <p className="text-sm text-text-body">
                Deseja realmente desbloquear este usuário? Ele poderá acessar o sistema novamente.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-border bg-bg-elevated p-6">
            <Button
              variant="secondary"
              onClick={() => {
                setShowUnblockModal(false);
                setSelectedUserId(null);
              }}
              disabled={unblockUserMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmUnblockUser}
              disabled={unblockUserMutation.isPending}
              className="bg-success text-on-tertiary shadow-none hover:bg-success/90"
            >
              {unblockUserMutation.isPending && <RefreshCw size={16} className="animate-spin" />}
              {unblockUserMutation.isPending ? 'Desbloqueando...' : 'Desbloquear'}
            </Button>
          </div>
        </Modal>

        {/* Delete User Confirmation Modal */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => {
            if (deleteUserMutation.isPending) return;
            setShowDeleteModal(false);
            setSelectedUserId(null);
          }}
          title="Excluir Usuário"
          size="sm"
        >
          <div className="p-6">
            <div className="flex items-start gap-3 rounded-lg border border-error/20 bg-error/10 p-3">
              <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-error/15 text-error">
                <Trash2 size={18} />
              </span>
              <div className="space-y-1.5">
                <p className="text-sm font-semibold text-error">
                  Atenção! Esta ação não pode ser desfeita.
                </p>
                <p className="text-sm text-text-body">
                  Deseja realmente excluir este usuário? Todos os dados associados serão
                  permanentemente removidos.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-border bg-bg-elevated p-6">
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedUserId(null);
              }}
              disabled={deleteUserMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={confirmDeleteUser}
              disabled={deleteUserMutation.isPending}
            >
              {deleteUserMutation.isPending && <RefreshCw size={16} className="animate-spin" />}
              {deleteUserMutation.isPending ? 'Excluindo...' : 'Excluir'}
            </Button>
          </div>
        </Modal>

        {/* Gestão de assinatura do produtor (unificado nesta página). */}
        {subscriptionUser && (
          <ManageSubscriptionModal
            userId={subscriptionUser.id}
            userName={subscriptionUser.name}
            onClose={() => setSubscriptionUser(null)}
          />
        )}
      </div>
    </Layout>
  );
}
